import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import { userRegister, userLogin, setTosRegister, resetStatus } from "features/common/auth/authSlice";
import Modal from "react-bootstrap/Modal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import Select from "react-select";
import dataCountry from "../../assets/json/country.json";
import { notifyWarning, notifySuccess, notifyError, notifyInfo } from "components/ToastMessage";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faMagicWandSparkles, faSpinner, faCheck, faLock, faPencilAlt } from "@fortawesome/free-solid-svg-icons";

const ModalRegisterSimple = (props) => {
	const dispatch = useDispatch();
	const authStatus = useSelector((state) => state.auth.status);
	const requirement = useSelector((state) => state.auth.tosRegister);
	const { productsList: cartItems } = useSelector((state) => state.cart);

	const [formData, setFormData] = useState({
		firstname: "",
		lastname: "",
		email: "",
		phonenumber: "",
		skipOtherFields: true,
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSendingNotification, setIsSendingNotification] = useState(false);
	const [isAutoLogging, setIsAutoLogging] = useState(false);
	const [phoneError, setPhoneError] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState({
		value: "ID",
		name: "Indonesia"
	});
	const [countryError, setCountryError] = useState(false);
	const [registerSuccess, setRegisterSuccess] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// OTP related states
	const [otpSent, setOtpSent] = useState(false);
	const [otpVerified, setOtpVerified] = useState(false);
	const [otpCode, setOtpCode] = useState('');
	const [otpCountdown, setOtpCountdown] = useState(0);
	const [otpSending, setOtpSending] = useState(false);
	const [otpVerifying, setOtpVerifying] = useState(false);

	// Check if "Daftar cepat" checkbox should be hidden
	const shouldHideQuickRegister = useMemo(() => {
		// Hide checkbox if cart is empty
		if (!cartItems || cartItems.length === 0) return true;

		// Check if cart only contains domain products (including whois)
		const onlyDomainProducts = cartItems.every(item =>
			item.type === "register" ||
			item.type === "transfer" ||
			item.type === "backorder-register" ||
			item.type === "backorder" ||
			item.type === "whois"
		);

		// If cart contains only domain/whois products, hide the checkbox
		if (onlyDomainProducts) return true;

		// Check if there are hosting/reseller/vps/ssl products with billing cycle < 1 year
		const hasShortTermHostingProducts = cartItems.some(item =>
			(item.type === "hostingaccount" ||
				item.type === "reselleraccount" ||
				item.type === "emailsuite" ||
				item.type === "server" ||
				item.type === "vpn" ||
				item.type === "ssl") &&
			(item.billingcycle === "monthly" ||
				item.billingcycle === "quarterly" ||
				item.billingcycle === "semiannually")
		);

		// Hide checkbox if there are short-term hosting products
		if (hasShortTermHostingProducts) return true;

		// Don't hide checkbox otherwise
		return false;
	}, [cartItems]);

	// Check if there are hosting/reseller/vps/ssl products with billing cycle 1 year or more
	const hasLongTermHostingProducts = useMemo(() => {
		if (!cartItems || cartItems.length === 0) return false;

		return cartItems.some(item =>
			(item.type === "hostingaccount" ||
				item.type === "reselleraccount" ||
				item.type === "emailsuite" ||
				item.type === "server" ||
				item.type === "vpn" ||
				item.type === "ssl") &&
			(item.billingcycle === "annually" ||
				item.billingcycle === "biennially" ||
				item.billingcycle === "triennially")
		);
	}, [cartItems]);

	// Check if there are hosting/reseller/vps/ssl products with billing cycle < 1 year
	const hasShortTermHostingProducts = useMemo(() => {
		if (!cartItems || cartItems.length === 0) return false;

		return cartItems.some(item =>
			(item.type === "hostingaccount" ||
				item.type === "reselleraccount" ||
				item.type === "server" ||
				item.type === "ssl") &&
			(item.billingcycle === "monthly" ||
				item.billingcycle === "quarterly" ||
				item.billingcycle === "semiannually")
		);
	}, [cartItems]);

	// Determine default value for skipOtherFields based on cart
	const getDefaultSkipOtherFields = useMemo(() => {
		// If cart is empty, default to false (unchecked, show address fields)
		if (!cartItems || cartItems.length === 0) return false;

		// Check if cart only contains domain products (including whois)
		const onlyDomainProducts = cartItems.every(item =>
			item.type === "register" ||
			item.type === "transfer" ||
			item.type === "backorder-register" ||
			item.type === "backorder" ||
			item.type === "whois"
		);

		// If cart contains only domain/whois products, default to false (unchecked, show address fields)
		if (onlyDomainProducts) return false;

		// If there are short-term hosting products, default to false (unchecked, show address fields)
		if (hasShortTermHostingProducts) return false;

		// If there are long-term hosting products, default to true (checked, hide address fields)
		if (hasLongTermHostingProducts) return true;

		// Otherwise, default to false (unchecked, show address fields)
		return false;
	}, [cartItems, hasShortTermHostingProducts, hasLongTermHostingProducts]);

	const { register, handleSubmit, formState: { errors, isSubmitted }, watch, reset, setValue, clearErrors, trigger } = useForm({
		defaultValues: {
			skipOtherFields: getDefaultSkipOtherFields
		},
		mode: 'onSubmit' // Validasi hanya saat submit
	});

	const skipOtherFields = watch("skipOtherFields");

	// Update skipOtherFields when cart changes
	useEffect(() => {
		const newDefaultValue = getDefaultSkipOtherFields;
		setValue('skipOtherFields', newDefaultValue);
	}, [cartItems, setValue, getDefaultSkipOtherFields]);

	// Check if "Daftar cepat" checkbox should be hidden
	const hideQuickRegister = shouldHideQuickRegister;
	const hasLongTermHosting = hasLongTermHostingProducts;
	const hasShortTermHosting = hasShortTermHostingProducts;

	// Address is required when - simplified logic with useMemo for consistency
	const isAddressRequired = useMemo(() => {
		// If skipOtherFields is true (checkbox checked), address is never required
		if (skipOtherFields) return false;

		// If checkbox is visible and user unchecked it, fields are ALWAYS OPTIONAL
		// This overrides all other conditions - user choice takes precedence
		if (!hideQuickRegister && !skipOtherFields) return false;

		// Address is required only when checkbox is HIDDEN and:
		// 1. Cart is empty
		if (hideQuickRegister && (!cartItems || cartItems.length === 0)) return true;

		// 2. There are short-term hosting products (< 1 year)
		if (hideQuickRegister && hasShortTermHosting) return true;

		// 3. Cart contains only domain/whois products (hideQuickRegister is true)
		if (hideQuickRegister) return true;

		// Otherwise optional
		return false;
	}, [cartItems, hasShortTermHosting, hideQuickRegister, skipOtherFields]);

	// Function to filter only letters for firstname
	const handleFirstnameChange = (e) => {
		const value = e.target.value;
		// Only allow letters (no numbers, special characters, or spaces)
		const filteredValue = value.replace(/[^a-zA-Z]/g, '');
		setValue('firstname', filteredValue);
	};

	// Function to filter only letters and spaces for lastname
	const handleLastnameChange = (e) => {
		const value = e.target.value;
		// Only allow letters and spaces (no numbers or special characters)
		const filteredValue = value.replace(/[^a-zA-Z ]/g, '');
		setValue('lastname', filteredValue);
	};

	// Function to filter only letters and spaces for company
	const handleCompanyChange = (e) => {
		const value = e.target.value;
		// Allow letters, spaces, and common business characters (., -, &, etc.)
		const filteredValue = value.replace(/[^a-zA-Z0-9 .,&()-]/g, '');
		setValue('company', filteredValue);
	};

	// Function to filter only letters and spaces for city
	const handleCityChange = (e) => {
		const value = e.target.value;
		// Only allow letters and spaces (no numbers or special characters)
		const filteredValue = value.replace(/[^a-zA-Z ]/g, '');
		setValue('city', filteredValue);
	};

	// Function to filter only letters and spaces for state
	const handleStateChange = (e) => {
		const value = e.target.value;
		// Only allow letters and spaces (no numbers or special characters)
		const filteredValue = value.replace(/[^a-zA-Z ]/g, '');
		setValue('state', filteredValue);
	};

	// Function to generate random password
	const generatePassword = () => {
		const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%!&*';
		let password = '';
		for (let i = 0; i < 12; i++) {
			password += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		setValue('password', password);
		setValue('confirmPassword', password);
		return password;
	};

	// OTP Countdown Timer
	useEffect(() => {
		let interval = null;
		if (otpCountdown > 0) {
			interval = setInterval(() => {
				setOtpCountdown(countdown => countdown - 1);
			}, 1000);
		} else if (otpCountdown === 0 && otpSent) {
			clearInterval(interval);
		}
		return () => clearInterval(interval);
	}, [otpCountdown, otpSent]);

	// Function to send OTP
	const sendOtp = async () => {
		if (!formData.phonenumber) {
			setPhoneError(true);
			notifyWarning('Silakan masukkan nomor WhatsApp terlebih dahulu');
			return;
		}

		setOtpSending(true);
		try {
			const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/sendOtpWa.php?phonenumber=${encodeURIComponent(formData.phonenumber)}`);
			const result = await response.json();

			if (result.success) {
				setOtpSent(true);
				setOtpCountdown(180); // 3 minutes
				notifySuccess(result.message);

				if (result.bypass) {
					setOtpVerified(true); // Langsung anggap OTP sudah diverifikasi
				}
			} else {
				// Check if OTP is still active
				if (result.error && result.error.includes('masih aktif')) {
					// Extract remaining time from error message
					const timeMatch = result.error.match(/(\d+)\s+menit/);
					const remainingMinutes = timeMatch ? parseInt(timeMatch[1]) : 3;
					const remainingSeconds = remainingMinutes * 60;

					// Set OTP as sent and start countdown
					setOtpSent(true);
					setOtpCountdown(remainingSeconds);
					notifyWarning('Kode OTP masih aktif. Silakan verifikasi kode yang sudah dikirim sebelumnya.');
				} else {
					notifyError(result.error || 'Gagal mengirim kode OTP');
				}
			}
		} catch (error) {
			notifyError('Gagal mengirim kode OTP. Silakan coba lagi.');
		} finally {
			setOtpSending(false);
		}
	};

	// Function to verify OTP
	const verifyOtp = async () => {
		if (!otpCode || otpCode.length !== 6) {
			notifyWarning('Silakan masukkan kode OTP 6 digit');
			return;
		}

		if (!formData.phonenumber) {
			notifyWarning('Nomor WhatsApp tidak valid');
			return;
		}

		setOtpVerifying(true);
		try {
			const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/verifyOtpWa.php?phonenumber=${encodeURIComponent(formData.phonenumber)}&otp=${otpCode}`);
			const result = await response.json();

			if (result.success) {
				setOtpVerified(true);
				notifySuccess(result.message);
			} else {
				// Check for specific error messages
				if (result.error && result.error.includes('tidak ada OTP aktif')) {
					// OTP tidak aktif, reset state dan minta kirim ulang
					setOtpSent(false);
					setOtpVerified(false);
					setOtpCode('');
					setOtpCountdown(0);
					notifyWarning('Kode OTP sudah tidak aktif. Silakan kirim kode OTP baru.');
				} else if (result.error && result.error.includes('kadaluarsa')) {
					// OTP expired, reset state
					setOtpSent(false);
					setOtpCountdown(0);
					setOtpCode('');
					notifyWarning('Kode OTP sudah kadaluarsa. Silakan kirim kode baru.');
				} else {
					notifyError(result.error || 'Kode OTP tidak valid');
				}
			}
		} catch (error) {
			notifyError('Gagal memverifikasi kode OTP. Silakan coba lagi.');
		} finally {
			setOtpVerifying(false);
		}
	};

	// Function to reset OTP state and unlock phone number
	const resetOtpState = () => {
		setOtpSent(false);
		setOtpVerified(false);
		setOtpCode('');
		setOtpCountdown(0);
		setOtpSending(false);
		setOtpVerifying(false);
		notifySuccess('Nomor WhatsApp dapat diubah kembali');
	};

	// Monitor auth status after successful registration
	useEffect(() => {
		if (authStatus === "idle" && registerSuccess) {
			// Reset form and close modal
			setFormData({
				firstname: "",
				lastname: "",
				email: "",
				phonenumber: "",
				skipOtherFields: true,
			});
			setSelectedCountry({
				value: "ID",
				name: "Indonesia"
			});
			setRegisterSuccess(false);
			setIsSubmitting(false);
			setIsSendingNotification(false);
			setIsAutoLogging(false);

			// Reset form with default values
			reset({
				skipOtherFields: getDefaultSkipOtherFields
			});

			props.onClickDaftar(false);

			// Don't reload page - let user continue their flow
			console.log('Registration completed successfully - staying on current page');
		}
	}, [authStatus, registerSuccess, props, reset, getDefaultSkipOtherFields]);

	// Check OTP status when modal opens and phone number is filled
	useEffect(() => {
		if (props.show && formData.phonenumber && !otpSent && !otpVerified) {
			// Check if there's an active OTP for this phone number
			const checkOtpStatus = async () => {
				try {
					const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/checkActiveOtp.php?phonenumber=${encodeURIComponent(formData.phonenumber)}`);
					const result = await response.json();

					if (result.success && result.hasActiveOtp) {
						// There's an active OTP, show verification UI
						setOtpSent(true);
						setOtpCountdown(result.remainingTime || 180);
						notifyInfo('Kode OTP masih aktif. Silakan verifikasi kode yang sudah dikirim sebelumnya.');
					}
				} catch (error) {
					// Silent fail - don't show error for status check
					console.log('OTP status check failed:', error);
				}
			};

			checkOtpStatus();
		}
	}, [props.show, formData.phonenumber, otpSent, otpVerified]);

	// Auto-fill address fields when skipOtherFields checkbox changes
	useEffect(() => {
		if (skipOtherFields) {
			// Auto-fill with empty when checkbox is checked
			setValue('address1', '');
			setValue('city', '');
			setValue('state', '');
			setValue('postcode', '');
			setValue('company', '');

			// Clear any validation errors for address fields
			clearErrors(['address1', 'city', 'state', 'postcode', 'company']);

			// Trigger validation to update field states
			setTimeout(() => {
				trigger(['address1', 'city', 'state', 'postcode', 'company']);
			}, 100);
		} else {
			// When unchecked, clear all errors first, then trigger validation
			clearErrors(['address1', 'city', 'state', 'postcode', 'company']);
			setTimeout(() => {
				trigger(['address1', 'city', 'state', 'postcode', 'company']);
			}, 100);
		}
	}, [skipOtherFields, setValue, clearErrors, trigger]);

	// Re-trigger validation when isAddressRequired changes to update validation rules
	useEffect(() => {
		// Clear errors and re-trigger validation when requirement status changes
		clearErrors(['address1', 'city', 'state', 'postcode']);
		setTimeout(() => {
			trigger(['address1', 'city', 'state', 'postcode']);
		}, 50);
	}, [isAddressRequired, clearErrors, trigger, skipOtherFields, hideQuickRegister]);

	const handlerRegister = (e) => {
		// Prevent double submit
		if (isSubmitting || isSendingNotification || isAutoLogging) return;
		e.preventDefault();
		dispatch(resetStatus()); // Reset error redux di awal
		handleSubmit(async (data) => {
			// Enhanced validation checks
			if (!requirement) {
				notifyWarning("Silahkan menyetujui Terms of Service terlebih dahulu!");
				return;
			}

			if (!formData.phonenumber) {
				setPhoneError(true);
				notifyWarning("Silahkan masukkan nomor WhatsApp terlebih dahulu!");
				return;
			}

			if (!selectedCountry || !selectedCountry.value) {
				setCountryError(true);
				notifyWarning("Silahkan pilih negara terlebih dahulu!");
				return;
			}

			// Check OTP verification - enhanced check
			if (!otpSent) {
				notifyWarning("Silahkan kirim kode OTP WhatsApp terlebih dahulu!");
				return;
			}

			if (!otpVerified) {
				notifyWarning("Silahkan verifikasi nomor WhatsApp Anda terlebih dahulu!");
				return;
			}

			setIsSubmitting(true);

			try {
				// Use password from form
				const userPassword = data.password;

				// Prepare registration data
				const registrationData = {
					firstname: data.firstname,
					lastname: data.lastname || "N/A",
					email: data.email,
					phonenumber: formData.phonenumber,
					password: userPassword,
					password2: userPassword,
					country: selectedCountry.value,
					// Handle company and address fields based on skipOtherFields
					company: data.skipOtherFields ? "" : (data.company || ""),
					address1: data.skipOtherFields ? "N/A" : (data.address1 || "N/A"),
					address2: "",
					city: data.skipOtherFields ? "N/A" : (data.city || "N/A"),
					state: data.skipOtherFields ? "N/A" : (data.state || "N/A"),
					postcode: data.skipOtherFields ? "12345" : (data.postcode || "12345")
				};

				const result = await dispatch(userRegister(registrationData));

				// Check for successful registration - handle new API response structure
				const isSuccess = result.payload && (
					result.payload.code === 200 ||
					result.payload.result === 'success' ||
					(result.payload.data && result.payload.data.result === 'success')
				) && !result.payload.error;

				if (isSuccess) {
					// Extract clientid/userid from different possible response structures
					let userid = 'new_user'; // fallback

					// Handle new response structure with nested data
					if (result.payload.data?.clientid) {
						userid = result.payload.data.clientid;
					} else if (result.payload.data?.userid) {
						userid = result.payload.data.userid;
					} else if (result.payload.clientid) {
						userid = result.payload.clientid;
					} else if (result.payload.userid) {
						userid = result.payload.userid;
					} else if (result.payload.data?.owner_id) {
						userid = result.payload.data.owner_id;
					}

					// Send account notification via WhatsApp and Email
					setIsSendingNotification(true);
					try {
						const params = new URLSearchParams({
							firstname: data.firstname,
							lastname: data.lastname || "N/A",
							email: data.email,
							phonenumber: formData.phonenumber,
							password: userPassword,
							userid: userid,
							company: data.skipOtherFields ? "" : (data.company || ""),
							bypass: 'true' // Enable bypass mode by default
						});

						const response = await fetch(`${process.env.REACT_APP_BASE_API}order/apis/sendRegisterAccount.php?${params.toString()}`);

						if (!response.ok) {
							throw new Error(`HTTP error! status: ${response.status}`);
						}

						const notificationResult = await response.json();

						if (notificationResult.success) {
							// Show success notification to user
							notifySuccess(
								`Akun berhasil dibuat! Informasi akun telah dikirim ke WhatsApp (${formData.phonenumber}) dan email (${data.email}). Sedang login otomatis...`
							);

							// Auto-login after successful registration and notification
							setIsAutoLogging(true);
							try {
								const loginResult = await dispatch(userLogin([data.email, userPassword]));

								if (loginResult.payload && !loginResult.payload.error) {
									notifySuccess('Login berhasil! Selamat datang di Qwords.');
									setRegisterSuccess(true);
								} else {
									notifyWarning('Registrasi berhasil, namun auto-login gagal. Silakan login manual dengan password yang Anda buat.');
									setRegisterSuccess(true);
								}
							} catch (loginError) {
								notifyWarning('Registrasi berhasil, namun auto-login gagal. Silakan login manual dengan password yang Anda buat.');
								setRegisterSuccess(true);
							} finally {
								setIsAutoLogging(false);
							}
						} else {
							notifyWarning('Akun berhasil dibuat, namun notifikasi gagal dikirim. Anda dapat langsung login dengan password yang telah dibuat.');
							setRegisterSuccess(true);
						}
					} catch (notificationError) {
						notifyWarning('Akun berhasil dibuat, namun notifikasi gagal dikirim. Anda dapat langsung login dengan password yang telah dibuat.');
						setRegisterSuccess(true);
					} finally {
						setIsSendingNotification(false);
					}
				} else {
					// Robust error handling
					let errorMsg = 'Registrasi gagal. Silakan coba lagi.';
					if (result.payload?.error) {
						if (typeof result.payload.error === 'string') {
							errorMsg = result.payload.error;
						} else if (typeof result.payload.error?.message === 'string') {
							errorMsg = result.payload.error.message;
						}
					} else if (result.error && typeof result.error.message === 'string') {
						errorMsg = result.error.message;
					}
					notifyError(errorMsg);
					dispatch(resetStatus());
				}
			} finally {
				setIsSubmitting(false);
			}
		})();
	};

	const handlerChangePhone = (value) => {
		// Prevent changing phone number if OTP is already verified
		if (otpVerified) {
			notifyWarning('Nomor WhatsApp tidak dapat diubah setelah verifikasi OTP berhasil');
			return;
		}

		// Check if phone number actually changed
		if (formData.phonenumber === value) {
			return;
		}

		setFormData({
			...formData,
			phonenumber: value,
		});
		setPhoneError(false);

		// Reset OTP state when phone number changes
		setOtpSent(false);
		setOtpVerified(false);
		setOtpCode('');
		setOtpCountdown(0);
	};

	const handlerChangeCountry = (option) => {
		setSelectedCountry(option);
		setCountryError(false);
	};

	const checkHandler = (e) => {
		const requirement = e.target;

		if (requirement.id === "tos") {
			dispatch(setTosRegister(requirement.checked ? true : false));
		}
	};

	const handleModalClose = () => {
		// Reset all state when modal is closed
		setFormData({
			firstname: "",
			lastname: "",
			email: "",
			phonenumber: "",
			skipOtherFields: true,
		});
		setIsSubmitting(false);
		setIsSendingNotification(false);
		setIsAutoLogging(false);
		setPhoneError(false);
		setSelectedCountry({
			value: "ID",
			name: "Indonesia"
		});
		setCountryError(false);
		setRegisterSuccess(false);

		// Reset OTP state
		setOtpSent(false);
		setOtpVerified(false);
		setOtpCode('');
		setOtpCountdown(0);
		setOtpSending(false);
		setOtpVerifying(false);

		// Reset form with default values
		reset({
			skipOtherFields: getDefaultSkipOtherFields
		});

		props.onHide();
	};

	const renderTosCheckbox = () => (
		<div className="form-check mb-3" id="tosRegister">
			<div className=" align-items-start">
				<input
					type="checkbox"
					className="form-check-input mt-1"
					name="tos"
					id="tos"
					value="tos"
					onChange={checkHandler}
					checked={requirement}
				/>
				<label htmlFor="tos" className="ms-3 form-check-label">
					Saya telah membaca dan menyetujui{" "}
					<a
						className="text-decoration-none"
						href={`${process.env.REACT_APP_QWORDS_API}master-service-agreement/?tab-content=terms-of-service`}
						target="_blank"
						rel="noreferrer"
					>
						<span className="text-qw-primary fw-bold">
							Term of Service
						</span>
					</a>
				</label>
			</div>
		</div>
	);

	return (
		<Modal
			show={props.show}
			onHide={handleModalClose}
			backdrop="static"
			keyboard={false}
			size="md"
			aria-labelledby="example-custom-modal-styling-title"
			centered
		>
			<Modal.Header closeButton>
				<Modal.Title id="example-custom-modal-styling-title">
					Daftar Akun Qwords
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form name="registerForm" autoComplete="on">
					{/* Main Fields */}
					<div className="form">
						<div className="row mb-3">
							<div className="col-6">
								<label htmlFor="firstname" className="form-label label-qwords">
									Nama Depan <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									name="firstname"
									id="firstname"
									placeholder="Nama depan"
									className={`form-control input-modal ${errors.firstname ? 'is-invalid' : ''}`}
									autoComplete="given-name"
									{...register("firstname", {
										required: "Nama Depan tidak boleh kosong",
										pattern: {
											value: /^[a-zA-Z]+$/,
											message: "Nama depan hanya boleh menggunakan huruf"
										},
										minLength: {
											value: 2,
											message: "Nama depan minimal 2 huruf"
										}
									})}
									onChange={handleFirstnameChange}
								/>
								{errors.firstname && <div className="invalid-feedback">{errors.firstname.message}</div>}
							</div>
							<div className="col-6">
								<label htmlFor="lastname" className="form-label label-qwords">
									Nama Belakang
								</label>
								<input
									type="text"
									name="lastname"
									id="lastname"
									placeholder="Nama belakang"
									className={`form-control input-modal ${errors.lastname ? 'is-invalid' : ''}`}
									autoComplete="family-name"
									{...register("lastname", {
										pattern: {
											value: /^[a-zA-Z ]*$/,
											message: "Nama belakang hanya boleh menggunakan huruf dan spasi"
										}
									})}
									onChange={handleLastnameChange}
								/>
								{errors.lastname && <div className="invalid-feedback">{errors.lastname.message}</div>}
							</div>
						</div>

						<div className="mb-3">
							<label htmlFor="email" className="form-label label-qwords">
								Email <span className="text-danger">*</span>
							</label>
							<input
								type="email"
								name="email"
								id="email"
								placeholder="Contoh: nama@email.com"
								className={`form-control input-modal ${errors.email ? 'is-invalid' : ''}`}
								autoComplete="email"
								{...register("email", {
									required: "Email tidak boleh kosong",
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: "Email tidak valid"
									}
								})}
							/>
							{errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
						</div>

						<div className="mb-3">
							<label htmlFor="no-telp" className="form-label label-qwords">
								Nomor WhatsApp <span className="text-danger">*</span>
								{otpVerified && (
									<span className="text-success ms-2">
										<FontAwesomeIcon icon={faLock} className="me-1" />
										Terkunci & Terverifikasi
									</span>
								)}
							</label>

							<div className=" gap-2">
								<div className="flex-grow-1">
									<PhoneInput
										localization="en"
										country="id"
										value={formData.phonenumber}
										onChange={handlerChangePhone}
										className={`phone-input ${phoneError ? 'is-invalid' : ''}`}
										disabled={otpSent} // Disable when OTP is sent (whether verified or not)
										style={otpSent ? {
											pointerEvents: 'none',
											opacity: 0.6
										} : {}}
									/>
								</div>

								{/* Show "Ganti No HP" button only when OTP is sent but NOT yet verified */}
								{otpSent && !otpVerified && (
									<button
										type="button"
										className="btn btn-outline-warning"
										onClick={resetOtpState}
										style={{
											height: '46px', // Match PhoneInput height
											whiteSpace: 'nowrap',
											fontSize: '14px'
										}}
										title="Ganti nomor WhatsApp"
									>
										<FontAwesomeIcon icon={faPencilAlt} className="me-1" />
										Ganti No HP
									</button>
								)}
							</div>

							{phoneError &&
								<div className="invalid-feedback d-block">Nomor WhatsApp tidak boleh kosong</div>
							}
						</div>

						{/* OTP Verification Section */}
						<div className="mb-3">
							<label className="form-label label-qwords">
								Verifikasi WhatsApp <span className="text-danger">*</span>
							</label>

							{!otpSent ? (
								<div>
									{!formData.phonenumber && (
										<div className="alert alert-warning py-2 mb-2" role="alert">
											<small>
												<strong>⚠️ Perhatian:</strong> Silakan isi nomor WhatsApp terlebih dahulu di atas sebelum mengirim kode OTP.
											</small>
										</div>
									)}
									<div className="d-grid">
										<button
											type="button"
											className="btn btn-outline-success"
											onClick={sendOtp}
											disabled={!formData.phonenumber || otpSending}
											title={!formData.phonenumber ? "Silakan isi nomor WhatsApp terlebih dahulu" : "Kirim kode OTP ke WhatsApp"}
										>
											{otpSending ? (
												<>
													<FontAwesomeIcon icon={faSpinner} spin className="me-2" />
													Mengirim Kode OTP...
												</>
											) : (
												'Kirim Kode OTP'
											)}
										</button>
									</div>
								</div>
							) : !otpVerified ? (
								<div>
									<div className="input-group mb-2">
										<input
											type="text"
											className="form-control"
											placeholder="Masukkan 6 digit kode OTP"
											value={otpCode}
											onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
											maxLength={6}
										/>
										<button
											type="button"
											className="btn btn-success"
											onClick={verifyOtp}
											disabled={otpCode.length !== 6 || otpVerifying}
										>
											{otpVerifying ? (
												<FontAwesomeIcon icon={faSpinner} spin />
											) : (
												'Verifikasi'
											)}
										</button>
									</div>

									<div className=" justify-content-between align-items-center">
										{otpCountdown > 0 ? (
											<small className="text-muted">
												Kode berlaku {Math.floor(otpCountdown / 60)}:{(otpCountdown % 60).toString().padStart(2, '0')}
											</small>
										) : (
											<small className="text-warning">
												⚠️ Kode OTP sudah kadaluarsa
											</small>
										)}

										<button
											type="button"
											className={`btn btn-sm ${otpCountdown > 0 ? 'btn-outline-secondary' : 'btn-warning'}`}
											onClick={sendOtp}
											disabled={otpSending}
											title={otpCountdown > 0 ? 'Kirim ulang kode OTP' : 'Kode expired, kirim ulang'}
										>
											{otpSending ? (
												<>
													<FontAwesomeIcon icon={faSpinner} spin className="me-1" />
													Mengirim...
												</>
											) : otpCountdown > 0 ? (
												'Kirim Ulang'
											) : (
												<>
													🔄 Kirim Ulang
												</>
											)}
										</button>
									</div>

									{/* Show clear message when OTP expired */}
									{otpCountdown === 0 && (
										<div className="mt-2">
											<small className="text-muted">
												Kode OTP sudah tidak berlaku. Klik "Kirim Ulang" untuk mendapatkan kode baru.
											</small>
										</div>
									)}
								</div>
							) : (
								<div className="alert alert-success py-2 mb-0">
									<FontAwesomeIcon icon={faCheck} className="me-2" />
									Nomor WhatsApp berhasil diverifikasi
								</div>
							)}
						</div>

						<div className="mb-3">
							<label htmlFor="country" className="form-label label-qwords">
								Negara <span className="text-danger">*</span>
							</label>
							<Select
								className={`country-select ${countryError ? 'is-invalid' : ''}`}
								classNamePrefix="select"
								options={dataCountry}
								value={selectedCountry}
								onChange={handlerChangeCountry}
								getOptionLabel={option => option.name}
								getOptionValue={option => option.value}
								isSearchable={true}
								isClearable={false}
								placeholder="Pilih negara"
							/>
							{countryError && (
								<div className="invalid-feedback d-block">
									Negara harus dipilih
								</div>
							)}
						</div>

						{/* Password Fields */}
						<div className="mb-3">
							<label htmlFor="password" className="form-label label-qwords">
								Password <span className="text-danger">*</span>
							</label>
							<div className="input-group">
								<input
									type={showPassword ? "text" : "password"}
									name="password"
									id="password"
									placeholder="Masukkan password"
									className={`form-control input-modal ${errors.password ? 'is-invalid' : ''}`}
									autoComplete="new-password"
									{...register("password", {
										required: "Password tidak boleh kosong",
										minLength: {
											value: 8,
											message: "Password minimal 8 karakter"
										},
										pattern: {
											value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
											message: "Password harus mengandung huruf besar, kecil, angka, dan simbol"
										}
									})}
								/>
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setShowPassword(!showPassword)}
									style={{ zIndex: 0 }}
								>
									<FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
								</button>
								<button
									type="button"
									className="btn btn-primary"
									onClick={generatePassword}
									title="Generate Password"
								>
									<FontAwesomeIcon icon={faMagicWandSparkles} />
								</button>
							</div>
							{errors.password && <div className="invalid-feedback d-block">{errors.password.message}</div>}
							<small className="text-muted">
								Password harus mengandung minimal 8 karakter dengan kombinasi huruf besar, kecil, angka, dan simbol.
							</small>
						</div>

						<div className="mb-3">
							<label htmlFor="confirmPassword" className="form-label label-qwords">
								Konfirmasi Password <span className="text-danger">*</span>
							</label>
							<div className="input-group">
								<input
									type={showConfirmPassword ? "text" : "password"}
									name="confirmPassword"
									id="confirmPassword"
									placeholder="Konfirmasi password"
									className={`form-control input-modal ${errors.confirmPassword ? 'is-invalid' : ''}`}
									autoComplete="new-password"
									{...register("confirmPassword", {
										required: "Konfirmasi password tidak boleh kosong",
										validate: (value) => {
											const password = watch('password');
											return password === value || "Password tidak sama";
										}
									})}
								/>
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									style={{ zIndex: 0 }}
								>
									<FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
								</button>
							</div>
							{errors.confirmPassword && <div className="invalid-feedback d-block">{errors.confirmPassword.message}</div>}
						</div>

						{/* Skip Other Fields Checkbox - Hidden when cart has short-term domain products */}
						{!hideQuickRegister && (
							<div className="form-check mb-3" id="quickRegisterCheckbox">
								<div className=" align-items-start">
									<input
										type="checkbox"
										className="form-check-input mt-1"
										id="skipOtherFields"
										{...register("skipOtherFields")}
									/>
									<label htmlFor="skipOtherFields" className="ms-3 form-check-label">
										<span className="text-success fw-bold">🚀 Daftar cepat</span>
										<br />
										<small className="text-muted">
											Buat akun tanpa isi nama perusahaan dan alamat. Data default akan terisi otomatis dan bisa diubah nanti. Lengkapi segera jika butuh faktur pajak.
										</small>
									</label>
								</div>
							</div>
						)}

						{/* Additional Fields - shown only when checkbox is not checked */}
						{!skipOtherFields && (
							<>
								<hr className="my-3" />
								<h6 className="mb-3 text-muted">
									{isAddressRequired ? "Informasi Tambahan (Required)" : "Informasi Tambahan (Opsional)"}
								</h6>

								<div className="mb-3">
									<label htmlFor="address1" className="form-label label-qwords">
										Alamat Lengkap {isAddressRequired && <span className="text-danger">*</span>}
									</label>
									<textarea
										placeholder="Contoh: Jl. Raya No. 123, RT/RW 01/02"
										className={`form-control input-modal ${errors.address1 && isSubmitted ? 'is-invalid' : ''}`}
										{...register("address1", {
											validate: (value) => {
												// Dynamic validation based on current state
												const currentlyRequired = !skipOtherFields && (
													(hideQuickRegister && (!cartItems || cartItems.length === 0)) ||
													(hideQuickRegister && hasShortTermHosting) ||
													hideQuickRegister
												);

												if (currentlyRequired && (!value || value.trim() === '')) {
													return "Alamat lengkap tidak boleh kosong";
												}
												return true;
											}
										})}
									></textarea>
									{errors.address1 && isSubmitted && <div className="invalid-feedback">{errors.address1.message}</div>}
								</div>

								<div className="row">
									<div className="col-md-6 mb-3">
										<label htmlFor="city" className="form-label label-qwords">
											Kota {isAddressRequired && <span className="text-danger">*</span>}
										</label>
										<input
											type="text"
											placeholder="Contoh: Jakarta Selatan"
											className={`form-control input-modal ${errors.city && isSubmitted ? 'is-invalid' : ''}`}
											{...register("city", {
												...(!skipOtherFields && {
													pattern: {
														value: /^[a-zA-Z ]*$/,
														message: "Nama kota hanya boleh menggunakan huruf dan spasi"
													}
												}),
												validate: (value) => {
													// Dynamic validation based on current state
													const currentlyRequired = !skipOtherFields && (
														(hideQuickRegister && (!cartItems || cartItems.length === 0)) ||
														(hideQuickRegister && hasShortTermHosting) ||
														hideQuickRegister
													);

													if (currentlyRequired && (!value || value.trim() === '')) {
														return "Kota tidak boleh kosong";
													}
													return true;
												}
											})}
											onChange={handleCityChange}
										/>
										{errors.city && isSubmitted && <div className="invalid-feedback">{errors.city.message}</div>}
									</div>
									<div className="col-md-6 mb-3">
										<label htmlFor="state" className="form-label label-qwords">
											Provinsi {isAddressRequired && <span className="text-danger">*</span>}
										</label>
										<input
											type="text"
											placeholder="Contoh: DKI Jakarta"
											className={`form-control input-modal ${errors.state && isSubmitted ? 'is-invalid' : ''}`}
											{...register("state", {
												...(!skipOtherFields && {
													pattern: {
														value: /^[a-zA-Z ]*$/,
														message: "Nama provinsi hanya boleh menggunakan huruf dan spasi"
													}
												}),
												validate: (value) => {
													// Dynamic validation based on current state
													const currentlyRequired = !skipOtherFields && (
														(hideQuickRegister && (!cartItems || cartItems.length === 0)) ||
														(hideQuickRegister && hasShortTermHosting) ||
														hideQuickRegister
													);

													if (currentlyRequired && (!value || value.trim() === '')) {
														return "Provinsi tidak boleh kosong";
													}
													return true;
												}
											})}
											onChange={handleStateChange}
										/>
										{errors.state && isSubmitted && <div className="invalid-feedback">{errors.state.message}</div>}
									</div>
								</div>

								<div className="mb-3">
									<label htmlFor="postcode" className="form-label label-qwords">
										Kode Pos {isAddressRequired && <span className="text-danger">*</span>}
									</label>
									<input
										type="number"
										placeholder="Contoh: 12345"
										className={`form-control input-modal ${errors.postcode && isSubmitted ? 'is-invalid' : ''}`}
										{...register("postcode", {
											validate: (value) => {
												// Dynamic validation based on current state
												const currentlyRequired = !skipOtherFields && (
													(hideQuickRegister && (!cartItems || cartItems.length === 0)) ||
													(hideQuickRegister && hasShortTermHosting) ||
													hideQuickRegister
												);

												if (currentlyRequired) {
													if (!value || value.trim() === '') {
														return "Kode pos tidak boleh kosong";
													}
													if (!/^\d{5}$/.test(value)) {
														return "Kode pos harus 5 digit angka";
													}
												}
												// Optional validation - still check pattern if value is provided
												if (value && value.trim() !== '' && !/^\d{5}$/.test(value)) {
													return "Kode pos harus 5 digit angka";
												}
												return true;
											}
										})}
									/>
									{errors.postcode && isSubmitted && <div className="invalid-feedback">{errors.postcode.message}</div>}
								</div>

								<div className="mb-3">
									<label htmlFor="company" className="form-label label-qwords">
										Nama Perusahaan
									</label>
									<input
										type="text"
										placeholder="Masukkan nama perusahaan (opsional)"
										className={`form-control input-modal ${errors.company ? 'is-invalid' : ''}`}
										{...register("company", {
											...(!skipOtherFields && {
												pattern: {
													value: /^[a-zA-Z0-9 .,&()-]*$/,
													message: "Nama perusahaan hanya boleh menggunakan huruf, angka, spasi, dan karakter bisnis umum (.,&,-,())"
												}
											})
										})}
										onChange={handleCompanyChange}
									/>
									{errors.company && <div className="invalid-feedback">{errors.company.message}</div>}
								</div>

							</>
						)}

						{renderTosCheckbox()}

						{/* Process Info */}
						{(isSubmitting || isSendingNotification || isAutoLogging) && (
							<div className="alert alert-warning">
								<div className=" align-items-start">
									<FontAwesomeIcon icon={faSpinner} spin className="me-2 mt-1" />
									<div>
										<strong>Proses Pendaftaran</strong>
										<br />
										<small>
											{isSubmitting && !isSendingNotification && !isAutoLogging && "Membuat akun Anda..."}
											{isSendingNotification && !isAutoLogging && "Mengirim informasi akun ke WhatsApp & email..."}
											{isAutoLogging && "Melakukan login otomatis..."}
										</small>
									</div>
								</div>
							</div>
						)}
					</div>
				</form>
			</Modal.Body>

			<Modal.Footer className="justify-content-center">
				<div className="row gap-3 w-100">
					<button
						type="button"
						className="btn btn-modal-login col-lg-12"
						onClick={handlerRegister}
						disabled={isSubmitting || isSendingNotification || isAutoLogging}
					>
						{isSubmitting || isSendingNotification || isAutoLogging ? (
							<>
								<Spinner
									as="span"
									animation="border"
									size="sm"
									role="status"
									aria-hidden="true"
									className="me-2"
								/>
								{isAutoLogging ? 'Login otomatis...' : isSendingNotification ? 'Mengirim notifikasi...' : 'Memproses registrasi...'}
							</>
						) : (
							'Daftar Sekarang'
						)}
					</button>

					<div className="col-lg-12">
						<div className="row text-center">
							<p className="mb-0 px-1">
								Sudah punya akun Qwords?{" "}
								<span
									onClick={() => {
										props.onClickLogin(true);
										props.onClickDaftar(false);
									}}
									className="text-decoration-none"
									style={{ color: "#ee4d30", cursor: "pointer" }}
								>
									Masuk
								</span>
							</p>
						</div>
					</div>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default ModalRegisterSimple; 